import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  Snackbar,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  OutlinedInput,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { Visibility as VisibilityIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTeacher } from '../../context/TeacherContext';
import apiAxiosInstance from '../../utils/axiosInstance';
import GoogleFormsImportButton from './GoogleFormsImport/GoogleFormsImportButton';
// import DocumentImportButton from './GoogleFormsImport/DocumentImportButton';
import AssignTestDialog from './AssignTestDialog';
import VideoHelpButton from '../Common/VideoHelpButton';

/**
 * Interfaces for component props and data structures
 */
interface Standard {
  _id: string;
  code: string;
  standard: string;
  description: string;
  shortDescription: string;
  id: string;
}

interface Question {
  _id: string;
  question: string;
  answers: string[];
  correctAnswer: string;
  explanation: string;
  standardReference: string;
  bloomsLevel: string;
}

interface AssessmentPassage {
  _id: string;
  title: string;
  genre: string;
  passage: string;
  isAIGenerated: boolean;
  includeAnswerKey: boolean;
  questions: Question[];
  passage2Questions?: Question[];
  combinedQuestions?: Question[];
  dateCreated: string;
  isPairedPassage?: boolean;
  passages?: Array<{
    topic: string;
    title?: string;
    content: string;
  }>;
  gradeLevel: string;
  standards: Array<Standard>;
}

interface TestBuilderFormData {
  title: string;
  description: string;
  passageIds: string[];
  gradeLevel: string;
  standards?: string[];
  tags?: string[];
  isPublic?: boolean;
}

interface Test {
  _id: string;
  title: string;
  description: string;
  passageIds: string[];
  gradeLevel: string;
  status: string;
  isPublic: boolean;
  createdAt: string;
  totalQuestions: number;
  standards?: string[];
  tags?: string[];
}

interface PassageViewDialogProps {
  open: boolean;
  passage: AssessmentPassage | null;
  onClose: () => void;
}

// Constants
const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];

/**
 * PassageViewDialog Component
 * Displays detailed view of a selected passage including questions and answers
 */
const PassageViewDialog: React.FC<PassageViewDialogProps> = ({ open, passage, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="div">
            {passage === null ? 'Loading...' : 
              passage.isPairedPassage && passage.passages ? 
                `Paired: ${passage.passages[0].topic} & ${passage.passages[1].topic}` :
                passage.title || 'Untitled'
            }
          </Typography>
          {passage?.isPairedPassage && (
            <Chip 
              label="Paired Passage" 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Genre: {passage?.genre}
            {passage?.standards && passage.standards.length > 0 && (
              <>
                {' • '}
                Standards: {passage.standards.map(std => std.code).join(', ')}
              </>
            )}
          </Typography>
        </Box>

        {passage?.isPairedPassage && passage.passages && passage.passages.length === 2 ? (
          <>
            {/* First Passage and its questions */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Passage 1: {passage.passages[0].topic}
              </Typography>
              <Typography variant="body1" paragraph>
                {passage.passages[0].content}
              </Typography>
              
              {passage.questions && passage.questions.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Questions for Passage 1
                  </Typography>
                  {passage.questions.map((question, index) => (
                    <QuestionDisplay 
                      key={index} 
                      question={question} 
                      index={index} 
                      showAnswerKey={passage.includeAnswerKey} 
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Second Passage and its questions */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Passage 2: {passage.passages[1].topic}
              </Typography>
              <Typography variant="body1" paragraph>
                {passage.passages[1].content}
              </Typography>
              
              {passage.passage2Questions && passage.passage2Questions.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Questions for Passage 2
                  </Typography>
                  {passage.passage2Questions.map((question, index) => (
                    <QuestionDisplay 
                      key={index} 
                      question={question} 
                      index={index + (passage.questions?.length || 0)} 
                      showAnswerKey={passage.includeAnswerKey} 
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Combined Questions */}
            {passage.combinedQuestions && passage.combinedQuestions.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Combined Questions
                </Typography>
                {passage.combinedQuestions.map((question, index) => (
                  <QuestionDisplay 
                    key={index} 
                    question={question} 
                    index={index + (passage.questions?.length || 0) + (passage.passage2Questions?.length || 0)} 
                    showAnswerKey={passage.includeAnswerKey} 
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Show passage content only if it's substantial (not just an import note) */}
            {passage?.passage && !passage.passage.startsWith('Imported from Google Form:') && (
              <Typography variant="body1" paragraph>
                {passage.passage}
              </Typography>
            )}
            
            {/* For Google Forms imports, show a note instead of minimal passage content */}
            {passage?.passage && passage.passage.startsWith('Imported from Google Form:') && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  This is an imported Google Form assessment. The questions below were imported from: <strong>{passage.title}</strong>
                </Typography>
                {passage.passage.split('\n\n')[1] && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Description: {passage.passage.split('\n\n')[1]}
                  </Typography>
                )}
              </Alert>
            )}
            
            {/* Single passage questions */}
            {passage?.questions && passage.questions.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Questions {passage.passage && passage.passage.startsWith('Imported from Google Form:') ? '(Imported from Google Form)' : ''}
                </Typography>
                {passage.questions.map((question, index) => (
                  <QuestionDisplay 
                    key={index} 
                    question={question} 
                    index={index} 
                    showAnswerKey={passage.includeAnswerKey} 
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * QuestionDisplay Component
 * Renders individual questions with answers and explanations
 */
const QuestionDisplay: React.FC<{
  question: Question;
  index: number;
  showAnswerKey: boolean;
}> = ({ question, index, showAnswerKey }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
      {index + 1}. {question.question}
    </Typography>
    {question.answers.map((answer, i) => (
      <Typography key={i} sx={{ ml: 3, mb: 0.5 }}>
        {String.fromCharCode(65 + i)}. {answer}
      </Typography>
    ))}
    {showAnswerKey && (
      <>
        <Typography sx={{ mt: 1, color: 'success.main' }}>
          Correct Answer: {question.correctAnswer}
        </Typography>
        {question.explanation && (
          <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
            Explanation: {question.explanation}
          </Typography>
        )}
      </>
    )}
    <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
      Standard: {question.standardReference}
    </Typography>
  </Box>
);

/**
 * TestBuilder Component
 * Main component for creating assessment tests
 * Allows teachers to select passages and configure test settings
 */
const TestBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { testId: routeTestId } = useParams();
  const { teacher } = useTeacher();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passages, setPassages] = useState<AssessmentPassage[]>([]);
  const [filteredPassages, setFilteredPassages] = useState<AssessmentPassage[]>([]);
  const [selectedFilterStandards, setSelectedFilterStandards] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPassage, setSelectedPassage] = useState<AssessmentPassage | null>(null);
  const [standards, setStandards] = useState<Standard[]>([]);
  const [tests, setTests] = useState<Test[]>([]);
  const [testsWithSubmissions, setTestsWithSubmissions] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [testToDelete, setTestToDelete] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTestId, setEditingTestId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });
  
  // Assign dialog state
  const [assignOpen, setAssignOpen] = useState(false);
  
  // All Students prompt state
  const [assignedDone, setAssignedDone] = useState(false);

  // Form state
  const [formData, setFormData] = useState<TestBuilderFormData>({
    title: '',
    description: '',
    passageIds: [],
    gradeLevel: teacher?.gradeLevel || '5',
    isPublic: true
  });

  /**
   * Initial data fetching - passages, standards, and tests
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [passagesResponse, standardsResponse, testsResponse] = await Promise.all([
          apiAxiosInstance.get('/api/assessment-passages'),
          apiAxiosInstance.get('/api/detailedstandards', {
            params: { gradeLevel: teacher?.gradeLevel || '5' }
          }),
          apiAxiosInstance.get('/api/tests')
        ]);

        setPassages(passagesResponse.data);
        setStandards(standardsResponse.data);
        
        // Get tests and check for submissions
        const fetchedTests: Test[] = testsResponse.data;
        setTests(fetchedTests);
        
        // Check which tests have submissions
        const submissionChecks = await Promise.all(
          fetchedTests.map((test: Test) => 
            apiAxiosInstance.get(`/api/tests/${test._id}/has-submissions`)
          )
        );
        
        const testsWithSubs = new Set(
          fetchedTests.filter((test: Test, index: number) => 
            submissionChecks[index].data.hasSubmissions
          ).map((test: Test) => test._id)
        );
        setTestsWithSubmissions(testsWithSubs as Set<string>);
        
        // Initially filter passages based on default grade level
        const initialFiltered = passagesResponse.data.filter(
          (p: AssessmentPassage) => p.gradeLevel === (teacher?.gradeLevel || '5')
        );
        setFilteredPassages(initialFiltered);
      } catch (err) {
        setError('Error loading data');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [teacher?.gradeLevel]);

  // Load existing test details when editing via /tests/edit/:testId
  useEffect(() => {
    const loadForEdit = async () => {
      if (!routeTestId) return;
      try {
        const res = await apiAxiosInstance.get(`/api/tests/${routeTestId}`);
        const t = res.data;
        setFormData({
          title: t.title || '',
          description: t.description || '',
          passageIds: t.passages ? t.passages.map((p: any) => p._id) : (t.passageIds || []),
          gradeLevel: t.gradeLevel || (teacher?.gradeLevel || '5'),
          isPublic: t.status === 'published'
        });
        setIsEditing(true);
        setEditingTestId(t._id);
      } catch (e) {
        console.error('Failed to load test for editing', e);
      }
    };
    loadForEdit();
  }, [routeTestId, teacher?.gradeLevel]);

  /**
   * Update standards when grade level changes
   */
  useEffect(() => {
    const fetchStandards = async () => {
      try {
        const response = await apiAxiosInstance.get('/api/detailedstandards', {
          params: { gradeLevel: formData.gradeLevel }
        });
        setStandards(response.data);
        // Clear selected standards when grade level changes
        setSelectedFilterStandards([]);
      } catch (err) {
        console.error('Error fetching standards:', err);
        setError('Failed to fetch standards');
      }
    };

    fetchStandards();
  }, [formData.gradeLevel]);

  /**
   * Filter passages based on grade level and selected standards
   */
  useEffect(() => {
    const filterPassages = () => {
      let filtered;
      
      // When no standards filter is selected, show all passages for the current grade.
      if (selectedFilterStandards.length === 0) {
        filtered = passages.filter(p => p.gradeLevel === formData.gradeLevel);
      } else {
        // If standards are selected, further filter passages by matching standards id.
        filtered = passages
          .filter(p => p.gradeLevel === formData.gradeLevel)
          .filter(passage => 
            passage.standards?.some(standard => 
              selectedFilterStandards.includes(standard.id)
            )
          );
      }
      
      setFilteredPassages(filtered);
    };

    filterPassages();
  }, [passages, formData.gradeLevel, selectedFilterStandards]);

  /**
   * Event Handlers
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Filter passages when grade level changes
    if (name === 'gradeLevel') {
      setFilteredPassages(passages.filter(p => p.gradeLevel === value));
      // Clear selected passages if grade level changes
      setFormData(prev => ({
        ...prev,
        [name]: value,
        passageIds: []
      }));
    }
  };

  const handlePassageSelection = (passageId: string, gradeLevel: string) => {
    setFormData(prev => {
      const newPassageIds = prev.passageIds.includes(passageId)
        ? prev.passageIds.filter(id => id !== passageId)
        : [...prev.passageIds, passageId];

      return {
        ...prev,
        passageIds: newPassageIds,
        gradeLevel: newPassageIds.length === 1 ? gradeLevel : prev.gradeLevel
      };
    });
  };

  // Publishing is automatic; no-op removed

  const handleViewPassage = (passage: AssessmentPassage) => {
    setSelectedPassage(passage);
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing && editingTestId) {
        // Update existing test
        await apiAxiosInstance.put(`/api/tests/${editingTestId}`, {
          ...formData,
          status: formData.isPublic ? 'published' : 'draft'  // Include status in update
        });
        
        // Refresh the tests list
        const response = await apiAxiosInstance.get('/api/tests');
        setTests(response.data);

        // Keep current test and form data; show success feedback
        setSnackbar({
          open: true,
          message: `Test "${formData.title}" updated successfully!`,
          severity: 'success'
        });
      } else {
        // Create new test
        const response = await apiAxiosInstance.post('/api/tests', {
          ...formData,
          status: formData.isPublic ? 'published' : 'draft'  // Include status in creation
        });
        
        // Capture created test id for Assign dialog
        const createdId = response?.data?.test?._id;
        if (createdId) {
          setEditingTestId(createdId);
          setIsEditing(true);
          // Allow navigating back to tests immediately after any assignment action
          setAssignedDone(true);
        }
        
        // Show success message
        setSnackbar({
          open: true,
          message: `Test "${formData.title}" created successfully!`,
          severity: 'success'
        });
        
        // Check if groups exist, then auto-assign or open dialog
        try {
          const groupsRes = await apiAxiosInstance.get('/api/groups');
          const groups = groupsRes.data || [];
          if (groups.length === 0) {
            // No groups exist - auto-assign to all students
            if (createdId) {
              try {
                await apiAxiosInstance.post('/api/assignments', {
                  testId: createdId,
                  assignmentType: 'allStudents',
                  options: { shuffleQuestions: false }
                });
                setAssignedDone(true);
                setSnackbar({
                  open: true,
                  message: 'Test assigned to all students successfully!',
                  severity: 'success'
                });
              } catch (error: any) {
                console.error('Error auto-assigning to all students:', error);
                setSnackbar({
                  open: true,
                  message: 'Error assigning test to all students',
                  severity: 'error'
                });
              }
            }
          } else {
            // Groups exist - open normal assign dialog
            setAssignOpen(true);
          }
        } catch {
          // If group check fails, fall back to opening the dialog
          setAssignOpen(true);
        }
      }
    } catch (err) {
      setError(isEditing ? 'Error updating test' : 'Error creating test');
      console.error(isEditing ? 'Error updating test:' : 'Error creating test:', err);
    }
  };

  const handleAssigned = () => {
    setAssignedDone(true);
    setSnackbar({ open: true, message: 'Assignment links created!', severity: 'success' });
  };

  // Removed old All Students prompt flow; auto-assign handled post-create when no groups exist

  const handleDeleteClick = (testId: string) => {
    setTestToDelete(testId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!testToDelete) return;

    try {
      await apiAxiosInstance.delete(`/api/tests/${testToDelete}`);
      setTests(prevTests => prevTests.filter(test => test._id !== testToDelete));
      setDeleteConfirmOpen(false);
      setTestToDelete(null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error deleting test');
    }
  };

  const handleEditTest = (test: Test) => {
    setFormData({
      title: test.title,
      description: test.description || '',
      passageIds: test.passageIds,
      gradeLevel: test.gradeLevel,
      standards: test.standards || [],
      tags: test.tags || [],
      isPublic: test.status === 'published'  // Sync isPublic with status
    });
    setIsEditing(true);
    setEditingTestId(test._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setFormData({
      title: '',
      description: '',
      passageIds: [],
      gradeLevel: teacher?.gradeLevel || '5',
      isPublic: true
    });
    setIsEditing(false);
    setEditingTestId(null);
  };

  const handleGoogleFormsImport = async (importedData: any) => {
    try {
      // Update form data with imported information
      setFormData(prev => ({
        ...prev,
        title: importedData.title || prev.title,
        description: importedData.description || prev.description,
      }));

      // If a passage was created, auto-select it and refresh the passages list
      if (importedData.passageId) {
        // Add the new passage ID to selected passages (prevent duplicates)
        setFormData(prev => ({
          ...prev,
          passageIds: prev.passageIds.includes(importedData.passageId) 
            ? prev.passageIds 
            : [...prev.passageIds, importedData.passageId]
        }));

        // Refresh passages list to show the new passage
        try {
          const passagesResponse = await apiAxiosInstance.get('/api/assessment-passages');
          setPassages(passagesResponse.data);
        } catch (err) {
          console.error('Error refreshing passages:', err);
        }
      }

      // Show success message
      setSnackbar({
        open: true,
        message: `Successfully imported "${importedData.title}" with ${importedData.questions.length} questions and added to current test`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error handling Google Forms import:', error);
      setSnackbar({
        open: true,
        message: 'Error importing Google Form',
        severity: 'error'
      });
    }
  };

  /**
   * Render Methods
   */
  const renderTestForm = () => (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            {isEditing ? 'Edit Test' : 'Create Test'}
          </Typography>
          <VideoHelpButton 
            url="https://www.youtube.com/watch?v=pWhSsTC89-Q"
            title="How to Create a Test"
            buttonLabel="Watch tutorial on creating tests"
            iconSize="small"
          />
        </Box>
        {isEditing && (
          <Button
            variant="outlined"
            onClick={handleCancelEdit}
          >
            Cancel Edit
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Test Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Grade Level</InputLabel>
            <Select
              value={formData.gradeLevel}
              label="Grade Level"
              name="gradeLevel"
              onChange={handleSelectChange}
              required
            >
              {GRADE_LEVELS.map((grade) => (
                <MenuItem key={grade} value={grade}>
                  Grade {grade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            multiline
            rows={2}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Selected Passages
        </Typography>
        {formData.passageIds.length === 0 ? (
          <Alert severity="info">
            No passages selected. Please select passages from the right panel.
          </Alert>
        ) : (
          <List>
            {formData.passageIds.map((passageId, index) => {
              const passage = passages.find(p => p._id === passageId);
              if (!passage) return null;
              
              return (
                <ListItem
                  key={passageId}
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handlePassageSelection(passageId, passage.gradeLevel)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography>
                        {index + 1}. {passage.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Genre: {passage.genre}
                          {passage.standards?.length > 0 && (
                            <> • Standards: {passage.standards.map(std => std.code).join(', ')}</>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {/* Import Sections */}
      {!isEditing && (
        <>
          <GoogleFormsImportButton
            onImport={handleGoogleFormsImport}
            gradeLevel={formData.gradeLevel}
            teacherStandards={teacher?.teachingStandards}
            state={teacher?.state}
          />
          {/* <DocumentImportButton
            onImport={handleGoogleFormsImport}
            gradeLevel={formData.gradeLevel}
          /> */}
        </>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          {isEditing && assignedDone && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/tests')}
            >
              Go to Tests
            </Button>
          )}
        </Box>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={formData.passageIds.length === 0}
        >
          {isEditing ? 'Update Test' : 'Create Test'}
        </Button>
      </Box>
    </form>
  );

  const renderPassagesList = () => (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {filteredPassages.map((passage) => (
          <Grid item xs={12} key={passage._id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" component="div">
                    {passage.isPairedPassage && passage.passages ? 
                      `Paired: ${passage.passages[0]?.topic || ''} & ${passage.passages[1]?.topic || ''}` :
                      passage.title || 'Untitled'
                    }
                  </Typography>
                  {passage.isPairedPassage && (
                    <Chip 
                      label="Paired Passage" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Genre: {passage.genre}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Questions: {
                      (passage.questions?.length || 0) + 
                      (passage.passage2Questions?.length || 0) + 
                      (passage.combinedQuestions?.length || 0)
                    }
                  </Typography>
                </Box>

                {passage.standards?.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Standards:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {passage.standards.map((standard, index) => (
                        <Tooltip 
                          key={index}
                          title={standard.description}
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={standard.standard}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Box>
                )}

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Questions: {
                    (passage.questions?.length || 0) + 
                    (passage.passage2Questions?.length || 0) + 
                    (passage.combinedQuestions?.length || 0)
                  }
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => handleViewPassage(passage)}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    variant={formData.passageIds.includes(passage._id) ? "contained" : "outlined"}
                    onClick={() => handlePassageSelection(passage._id, passage.gradeLevel)}
                    disabled={formData.passageIds.includes(passage._id)}
                  >
                    {formData.passageIds.includes(passage._id) ? 'Selected' : 'Select'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {filteredPassages.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">
              No passages available for Grade {formData.gradeLevel}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const [standardsMenuOpen, setStandardsMenuOpen] = useState(false);

  const renderStandardsFilter = () => (
    <Box sx={{ mb: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel>Filter by Standards</InputLabel>
        <Select
          multiple
          open={standardsMenuOpen}
          onOpen={() => setStandardsMenuOpen(true)}
          onClose={() => setStandardsMenuOpen(false)}
          value={selectedFilterStandards}
          onChange={(e) => {
            const value = e.target.value as string[];
            // Filter out header placeholder value
            const filteredValue = value.filter(v => v !== '__header__');
            setSelectedFilterStandards(filteredValue);
          }}
          input={<OutlinedInput label="Filter by Standards" />}
          renderValue={(selected) => (
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5,
              minHeight: 30,
              alignItems: 'center'
            }}>
              {selected.map((value) => {
                const standard = standards.find(s => s._id === value);
                return (
                  <Chip
                    key={value}
                    label={standard?.standard || ''}  // Only show the standard code in the chip
                    size="small"
                    sx={{
                      maxWidth: '100%',
                      height: 24,
                      '& .MuiChip-label': {
                        padding: '0 8px',
                        fontSize: '0.75rem',
                        lineHeight: 1.2
                      }
                    }}
                  />
                );
              })}
            </Box>
          )}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 400,
                maxWidth: 400,
              }
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          <MenuItem 
            value="__header__"
            disabled
            sx={{ 
              position: 'sticky', 
              top: 0, 
              backgroundColor: 'background.paper', 
              zIndex: 1, 
              borderBottom: '1px solid', 
              borderColor: 'divider', 
              py: 1, 
              minHeight: 'auto',
              cursor: 'default',
              '&:hover': { backgroundColor: 'background.paper' }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pointerEvents: 'none' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Select Standards</Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setStandardsMenuOpen(false);
                }}
                sx={{ pointerEvents: 'auto' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>
          {standards.map((standard) => (
            <MenuItem 
              key={standard._id} 
              value={standard._id}
              sx={{ 
                py: 0.75,
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                maxWidth: 400
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Checkbox 
                  checked={selectedFilterStandards.indexOf(standard._id) > -1} 
                  sx={{ pt: 0 }}
                  size="small"
                />
                <Box sx={{ ml: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      wordBreak: 'break-word'
                    }}
                  >
                    {standard.standard}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      mt: 0.5,
                      wordBreak: 'break-word'
                    }}
                  >
                    {standard.shortDescription}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {/* Show selected standards count */}
      {selectedFilterStandards.length > 0 && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mt: 1 }}
        >
          {selectedFilterStandards.length} standard{selectedFilterStandards.length > 1 ? 's' : ''} selected
        </Typography>
      )}
    </Box>
  );

  const renderTestList = () => {
    const availableTests = tests.filter(test => 
      test.status !== 'archived' && !testsWithSubmissions.has(test._id)
    );
    
    if (availableTests.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 4 }}>
          No available tests found. Create your first test using the form above.
        </Alert>
      );
    }

    return (
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Available Tests
        </Typography>
        
        {/* Help alert for draft tests */}
        {/* Draft help removed as tests are auto-published */}
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Grade Level</TableCell>
                <TableCell>Questions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableTests.map((test) => (
                <TableRow key={test._id}>
                  <TableCell>{test.title}</TableCell>
                  <TableCell>Grade {test.gradeLevel}</TableCell>
                  <TableCell>{test.totalQuestions} questions</TableCell>
                  <TableCell>
                    <Chip
                      label={test.status}
                      color={test.status === 'draft' ? 'warning' : 'success'}
                      size="small"
                    />
                    {test.isPublic && (
                      <Chip
                        label="Public"
                        color="info"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(test.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      {/* Test Link Button - only show for public/published tests */}
                      {(test.isPublic || test.status === 'published') && (
                        <Tooltip title="Copy test link for students">
                          <IconButton 
                            color="primary"
                            disabled={test.status === 'archived'}
                            onClick={() => {
                              const testUrl = `${window.location.origin}/test/${test._id}`;
                              navigator.clipboard.writeText(testUrl);
                              setSnackbar({
                                open: true,
                                message: 'Test link copied to clipboard!',
                                severity: 'success'
                              });
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {/* Edit Button */}
                      <Tooltip title={test.status === 'published' ? 'Published tests cannot be edited' : 'Edit test'}>
                        <span>
                          <IconButton 
                            onClick={() => handleEditTest(test)}
                            disabled={test.status === 'published'}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      
                      {/* Delete Button */}
                      <Tooltip title={test.status === 'published' ? 'Published tests cannot be deleted' : 'Delete test'}>
                        <span>
                          <IconButton 
                            color="error"
                            disabled={test.status === 'published'}
                            onClick={() => handleDeleteClick(test._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Remove top small header; use balanced section titles */}

      <Grid container spacing={3}>
        {/* Left Column - Test Creation Form */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 100px)', overflow: 'auto' }}>
            {renderTestForm()}
          </Paper>
        </Grid>

        {/* Right Column - Available Passages */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 100px)', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Available Passages
            </Typography>
            {renderStandardsFilter()}
            {renderPassagesList()}
          </Paper>
        </Grid>
      </Grid>

      {/* Test List Section */}
      {renderTestList()}

      {/* Dialogs */}
      <PassageViewDialog
        open={dialogOpen}
        passage={selectedPassage}
        onClose={() => setDialogOpen(false)}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this test? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* All Students Assignment Prompt removed - replaced by automatic assignment when no groups exist */}

      {/* Assign Dialog - if editing, use current id; if created, API response contained new id in navigate block */}
      <AssignTestDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        testId={editingTestId || ''}
        testTitle={formData.title}
        onAssigned={handleAssigned}
      />
    </Box>
  );
};

export default TestBuilder; 